import React, { useState, useEffect } from 'react';
import { 
  X, 
  FileText, 
  PenTool, 
  Lightbulb,
  Search,
  Calendar,
  CheckCircle,
  Plus
} from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../../../lib/supabase';
import { toast } from 'react-hot-toast';

const ContentAssignmentModal = ({ 
  isOpen, 
  onClose, 
  userId, 
  slotId,
  contentType, // 'essay', 'freewrite', or 'idea'
  onAssign 
}) => {
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContent, setSelectedContent] = useState(null);

  // Load content based on type
  useEffect(() => {
    if (isOpen && userId) {
      loadContent();
    }
  }, [isOpen, userId, contentType]);

  const loadContent = async () => {
    try {
      setLoading(true);
      let data = [];

      switch (contentType) {
        case 'essay':
          const { data: essays, error: essayError } = await supabase
            .from('college_journey_essays')
            .select('*')
            .eq('user_id', userId)
            .eq('deleted', false)
            .order('updated_at', { ascending: false });
          
          if (essayError) throw essayError;
          data = essays || [];
          break;

        case 'freewrite':
          const { data: freewrites, error: freewriteError } = await supabase
            .from('college_journey_freewrites')
            .select('*')
            .eq('user_id', userId)
            .order('date', { ascending: false });
          
          if (freewriteError) throw freewriteError;
          data = freewrites || [];
          break;

        case 'idea':
          const { data: ideas, error: ideaError } = await supabase
            .from('college_journey_essay_ideas')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });
          
          if (ideaError) throw ideaError;
          data = ideas || [];
          break;
      }

      setContent(data);
    } catch (error) {
      console.error('Error loading content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredContent = () => {
    if (!searchTerm) return content;
    
    const term = searchTerm.toLowerCase();
    return content.filter(item => {
      switch (contentType) {
        case 'essay':
          return item.title?.toLowerCase().includes(term) || 
                 item.content?.toLowerCase().includes(term);
        case 'freewrite':
          return item.prompt?.toLowerCase().includes(term) || 
                 item.content?.toLowerCase().includes(term);
        case 'idea':
          return item.title?.toLowerCase().includes(term) || 
                 item.description?.toLowerCase().includes(term) ||
                 item.tags?.some(tag => tag.toLowerCase().includes(term));
        default:
          return false;
      }
    });
  };

  const handleAssign = async () => {
    if (!selectedContent) {
      toast.error('Please select content to assign');
      return;
    }

    try {
      setLoading(true);
      await onAssign(slotId, contentType, selectedContent.id);
      // Don't close here - let parent handle it after refresh
      // onClose();
      // Don't show toast here - parent will show it
      // toast.success(`${getContentTypeLabel()} assigned successfully`);
    } catch (error) {
      console.error('Error assigning content:', error);
      toast.error('Failed to assign content');
    } finally {
      setLoading(false);
    }
  };

  const getContentTypeLabel = () => {
    switch (contentType) {
      case 'essay': return 'Essay';
      case 'freewrite': return 'Freewrite';
      case 'idea': return 'Idea';
      default: return 'Content';
    }
  };

  const getContentIcon = () => {
    switch (contentType) {
      case 'essay': return FileText;
      case 'freewrite': return PenTool;
      case 'idea': return Lightbulb;
      default: return FileText;
    }
  };

  const ContentIcon = getContentIcon();
  const filteredContent = getFilteredContent();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900 bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-stone-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-azure-100 rounded-lg">
                <ContentIcon className="w-5 h-5 text-azure-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">
                  Select {getContentTypeLabel()} to Attach
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  Choose content to attach to this essay slot
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-stone-500" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="mt-4">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400" />
              <input
                type="text"
                placeholder={`Search ${contentType}s...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
              />
            </div>
          </div>
        </div>

        {/* Content List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azure-600"></div>
            </div>
          ) : filteredContent.length === 0 ? (
            <div className="text-center py-12">
              <ContentIcon className="w-12 h-12 text-stone-400 mx-auto mb-3" />
              <p className="text-slate-600">No {contentType}s found</p>
              <p className="text-sm text-stone-500 mt-1">
                Create some {contentType}s first to attach them to slots
              </p>
            </div>
          ) : (
            <div className="grid gap-3">
              {filteredContent.map((item) => (
                <ContentCard
                  key={item.id}
                  item={item}
                  contentType={contentType}
                  isSelected={selectedContent?.id === item.id}
                  onSelect={() => setSelectedContent(item)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-stone-200">
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-700 hover:bg-stone-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={!selectedContent || loading}
              className={`
                px-4 py-2 rounded-lg font-medium transition-colors
                ${selectedContent && !loading
                  ? 'bg-azure-600 text-white hover:bg-azure-700'
                  : 'bg-stone-200 text-stone-400 cursor-not-allowed'
                }
              `}
            >
              {loading ? 'Assigning...' : 'Assign to Slot'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Content Card Component
const ContentCard = ({ item, contentType, isSelected, onSelect }) => {
  const getCardContent = () => {
    switch (contentType) {
      case 'essay':
        return (
          <>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-medium text-slate-900">
                  {item.title || 'Untitled Essay'}
                </h4>
                <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                  {item.content?.substring(0, 150)}...
                </p>
              </div>
              <div className="ml-4 text-right">
                <div className="text-sm text-stone-500">
                  {item.word_count || 0} words
                </div>
                <div className="text-xs text-stone-400 mt-1">
                  {format(new Date(item.updated_at), 'MMM d, yyyy')}
                </div>
              </div>
            </div>
            {item.college_names && (
              <div className="mt-2 flex flex-wrap gap-1">
                {item.college_names.map((college, idx) => (
                  <span key={idx} className="text-xs px-2 py-1 bg-azure-100 text-azure-700 rounded">
                    {college}
                  </span>
                ))}
              </div>
            )}
          </>
        );

      case 'freewrite':
        return (
          <>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-stone-400" />
                  <h4 className="font-medium text-slate-900">
                    {format(new Date(item.date), 'MMMM d, yyyy')}
                  </h4>
                </div>
                {item.prompt && (
                  <p className="text-sm text-slate-600 mt-1">
                    Prompt: {item.prompt}
                  </p>
                )}
                <p className="text-sm text-stone-500 mt-1 line-clamp-2">
                  {item.content?.substring(0, 150)}...
                </p>
              </div>
              <div className="ml-4 text-right">
                <div className="text-sm text-stone-500">
                  {item.content?.split(' ').length || 0} words
                </div>
              </div>
            </div>
          </>
        );

      case 'idea':
        return (
          <>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-medium text-slate-900">
                  {item.title}
                </h4>
                <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                  {item.description}
                </p>
                {item.tags && item.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {item.tags.map((tag, idx) => (
                      <span key={idx} className="text-xs px-2 py-1 bg-violet-100 text-violet-700 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="ml-4">
                <div className={`
                  text-xs px-2 py-1 rounded
                  ${item.impact_score >= 8 ? 'bg-emerald-100 text-emerald-700' :
                    item.impact_score >= 5 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-stone-100 text-slate-700'}
                `}>
                  Impact: {item.impact_score}/10
                </div>
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div
      onClick={onSelect}
      className={`
        p-4 rounded-lg border-2 cursor-pointer transition-all
        ${isSelected 
          ? 'border-azure-500 bg-azure-50' 
          : 'border-stone-200 bg-white hover:border-stone-300'
        }
      `}
    >
      <div className="flex items-start gap-3">
        {isSelected && (
          <CheckCircle className="w-5 h-5 text-azure-600 flex-shrink-0 mt-0.5" />
        )}
        <div className="flex-1">
          {getCardContent()}
        </div>
      </div>
    </div>
  );
};

export default ContentAssignmentModal;